import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import API_ENDPOINTS from "../config/api";

const CheckPasswordPage = () => {
  const navigate = useNavigate();
  const { applicationNumber } = useParams();
  const isDesktop = useMediaQuery({ minWidth: 768 });

  const [copySuccess, setCopySuccess] = useState(false);
  const [displayAccessCode, setDisplayAccessCode] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    if (applicationNumber) {
      fetchApplicationData();
    } else {
      setInitialLoading(false);
    }
  }, [applicationNumber]);

  // applicationNumber로 신청 정보 가져오기
  const fetchApplicationData = async () => {
    setInitialLoading(true);
    try {
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/applications/number/${applicationNumber}`
      );

      if (!response.ok) {
        throw new Error("신청 정보를 가져오는데 실패했습니다.");
      }

      const data = await response.json();
      setDisplayAccessCode(data.accessCode); // 응답의 accessCode를 화면에 표시
    } catch (err) {
      setError("신청 정보를 가져오는데 실패했습니다.");
    } finally {
      setInitialLoading(false);
    }
  };

  // 다음 페이지로 이동 (API 호출 없이)
  const handleContinue = () => {
    // 다음 페이지로 이동
    navigate("/check-status"); // 또는 실제 이동해야 할 페이지
  };

  // 뒤로 가기
  const handleBack = () => {
    navigate(-1);
  };

  // 번호 복사 기능
  const handleCopy = () => {
    if (displayAccessCode) {
      // 모바일 지원을 위한 fallback 복사 방법 추가
      if (navigator.clipboard) {
        navigator.clipboard
          .writeText(displayAccessCode)
          .then(() => {
            showCopySuccess();
          })
          .catch((err) => {
            fallbackCopy();
          });
      } else {
        fallbackCopy();
      }
    }
  };

  // 폴백 복사 메소드 (모바일 지원을 위해)
  const fallbackCopy = () => {
    // 임시 텍스트 영역 생성
    const textArea = document.createElement("textarea");
    textArea.value = displayAccessCode;

    // 화면 밖으로 위치시키기
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);

    // 선택 및 복사
    textArea.focus();
    textArea.select();

    let success = false;
    try {
      success = document.execCommand("copy");
    } catch (err) {}

    // 정리
    document.body.removeChild(textArea);

    // 성공 표시
    if (success) {
      showCopySuccess();
    }
  };

  // 복사 성공 표시
  const showCopySuccess = () => {
    setCopySuccess(true);
    setShowToast(true);

    // 2초 후 표시 제거
    setTimeout(() => {
      setCopySuccess(false);
      setShowToast(false);
    }, 2000);
  };

  // 애니메이션 변수
  const pageVariants = {
    initial: { opacity: 0 },
    in: {
      opacity: 1,
      transition: { duration: 0.5 },
    },
    out: {
      opacity: 0,
      transition: { duration: 0.3 },
    },
  };

  const contentVariants = {
    initial: { opacity: 0, y: 20 },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  // 토스트 애니메이션
  const toastVariants = {
    initial: { opacity: 0, y: 50 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
    exit: {
      opacity: 0,
      y: 50,
      transition: {
        duration: 0.3,
      },
    },
  };

  // 초기 로딩 중 표시
  if (initialLoading) {
    return (
      <PageBackground>
        <Container
          variants={pageVariants}
          initial="initial"
          animate="in"
          exit="out"
        >
          <Header>
            <BackButton onClick={handleBack}>
              <BackIcon>&#10094;</BackIcon>
            </BackButton>
            <HeaderTitle>신청번호 확인</HeaderTitle>
          </Header>
          <Content>
            <LoadingContainer>
              <Spinner />
              <LoadingText>신청 정보를 불러오는 중입니다...</LoadingText>
            </LoadingContainer>
          </Content>
        </Container>
      </PageBackground>
    );
  }

  // 에러 발생 시
  if (error) {
    return (
      <PageBackground>
        <Container
          variants={pageVariants}
          initial="initial"
          animate="in"
          exit="out"
        >
          <Header>
            <BackButton onClick={handleBack}>
              <BackIcon>&#10094;</BackIcon>
            </BackButton>
            <HeaderTitle>신청번호 확인</HeaderTitle>
          </Header>
          <Content>
            <ErrorContainer>
              <ErrorIcon>⚠️</ErrorIcon>
              <ErrorTitle>신청 정보를 찾을 수 없습니다</ErrorTitle>
              <ErrorDescription>
                유효한 신청 URL을 통해 접근해주세요.
              </ErrorDescription>
              <HomeButton onClick={() => navigate("/")}>
                홈으로 돌아가기
              </HomeButton>
            </ErrorContainer>
          </Content>
        </Container>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <div>
            <Title>조회 비밀번호</Title>
          </div>
          <Header>
            <SubTitle>집단소송 신청내역 조회에</SubTitle>
            <SubTitle>필요한 비밀번호입니다.</SubTitle>
            <SubTitle2>내역 조회 시 해당 비밀번호를 입력해주세요.</SubTitle2>
          </Header>

          <ApplicationNumberSection>
            <SectionLabel>신청내역 조회 비밀번호</SectionLabel>
            <ApplicationNumberDisplay>
              <AccessCodeDisplay>{displayAccessCode}</AccessCodeDisplay>
              <CopyButton onClick={handleCopy}>
                <CopyIcon />
                {copySuccess ? "복사됨" : "복사하기"}
              </CopyButton>
            </ApplicationNumberDisplay>
          </ApplicationNumberSection>
          <ButtonsContainer>
            <ActionButton onClick={handleContinue}>신청 조회하기</ActionButton>
          </ButtonsContainer>

          {/* 토스트 알림 */}
          <AnimatePresence>
            {showToast && (
              <Toast
                variants={toastVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <CheckIcon>✓</CheckIcon>
                비밀번호가 클립보드에 복사되었습니다
              </Toast>
            )}
          </AnimatePresence>
        </Content>
      </Container>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f8f8f8;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #fff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f8f8f8;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;
const Header = styled.div`
  width: 100%;
  margin-top: 20px;
  margin-bottom: 20px;
  color: #333;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const BackButton = styled.button`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BackIcon = styled.span`
  font-size: 20px;
  color: #333;
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 30px 20px;
  box-sizing: border-box;
`;

const TitleSection = styled.div`
  width: 100%;
  margin-bottom: 40px;
  text-align: left;
`;

const Title = styled.h1`
  color: #00285b;
  text-align: center;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.34px;
  margin: 5px 0;
  padding-top: 10px;
  word-break: keep-all;
`;

const SubTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  word-break: keep-all;
  color: #2e3840;
  text-align: left;
  width: 100%;
`;
const SubTitle2 = styled.h2`
  margin-top: 16px;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.3;
  margin-bottom: 20px;
  word-break: keep-all;
  color: #4b5563;
  text-align: left;
  width: 100%;
`;

const ApplicationNumberSection = styled.div`
  width: 100%;
  margin-bottom: 30px;
  display: flex;
  flex-direction: column;
`;

const SectionLabel = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
`;

const ApplicationNumberDisplay = styled.div`
  display: flex;
  width: 100%;
  height: 72px;
  padding: 12px 16px;
  justify-content: space-between;
  align-items: center;
  gap: 62px;
  flex-shrink: 0;
  border-radius: 8px;
  border: 1px solid #cbcfd5;
  background: #fff;
`;

const AccessCodeDisplay = styled.div`
  width: 148px;
  flex-shrink: 0;
  color: #2e3840;
  font-family: Pretendard;
  font-size: 32px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: 5.12px;
`;

const CopyButton = styled.button`
  display: flex;
  padding: 8px 8px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  background: #f2f9ff;
  border: none;
  cursor: pointer;
  color: #0075ff;
  font-size: 14px;
  font-weight: 500;
  -webkit-tap-highlight-color: transparent; /* 모바일 터치 하이라이트 제거 */
  touch-action: manipulation; /* 모바일 터치 최적화 */
  user-select: none; /* 텍스트 선택 방지 */

  &:active {
    background: #e0f0ff; /* 터치 시 피드백 */
    transform: scale(0.98); /* 약간의 눌림 효과 */
  }
`;

const CopyIcon = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-right: 5px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%230075FF' viewBox='0 0 24 24'%3E%3Cpath d='M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z'/%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  background-color: #f5f9ff;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 40px;
`;

const InfoIcon = styled.span`
  font-size: 18px;
  margin-right: 10px;
  margin-top: 2px;
`;

const InfoText = styled.p`
  font-size: 14px;
  color: #555;
  margin: 0;
  line-height: 1.5;
`;
const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 180px;
  padding: 0 0px;

  @media (min-width: 769px) {
    padding: 0;
    background-color: transparent;
  }
`;

const ActionButton = styled(motion.button)`
  display: flex;
  width: 100%;
  max-width: 400px;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  background: #0075ff;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  @media (min-width: 769px) {
    max-width: 430px;
    margin: 0 auto;
  }
`;

// 로딩 스타일 컴포넌트
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  width: 100%;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 117, 255, 0.1);
  border-radius: 50%;
  border-left-color: #0075ff;
  animation: spin 1s ease infinite;
  margin-bottom: 20px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  font-size: 16px;
  color: #666;
  text-align: center;
`;

// 에러 스타일 컴포넌트
const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  width: 100%;
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
`;

const ErrorTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
`;

const ErrorDescription = styled.p`
  font-size: 15px;
  color: #666;
  margin-bottom: 30px;
`;

const HomeButton = styled.button`
  background-color: #0075ff;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
`;

// 토스트 알림 스타일
const Toast = styled(motion.div)`
  position: fixed;
  bottom: 10px;
  left: 12%;

  /* 데스크탑에서는 32%로 위치 조정 */
  @media (min-width: 768px) {
    left: 32%;
    max-width: 400px;
    min-width: 40%;
  }

  transform: translateX(-50%);
  background-color: rgba(46, 56, 64, 0.9);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 14px;
  z-index: 1000;
  min-width: 200px;
  max-width: 90%;
`;

const CheckIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-color: #0075ff;
  color: white;
  border-radius: 50%;
  margin-right: 8px;
  font-size: 16px;
  font-weight: bold;
`;

export default CheckPasswordPage;
